"use client"

import Image from "next/image"
import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { ArrowLeft, ArrowRight } from "lucide-react"
import toast from "react-hot-toast"

export default function Header({ items }) {
  const [index, setIndex] = useState(0)
  const [loadedImages, setLoadedImages] = useState({})
  const slideDuration = 5000

  useEffect(() => {
    if (!items || items.length <= 1) return

    const slideTimer = setTimeout(() => {
      setIndex((prevIndex) => (prevIndex + 1) % items.length)
    }, slideDuration)

    return () => clearTimeout(slideTimer)
  }, [index, items])

  const handlePrev = () => {
    setIndex((prevIndex) => (prevIndex - 1 + items.length) % items.length)
  }

  const handleNext = () => {
    setIndex((prevIndex) => (prevIndex + 1) % items.length)
  }

  return (
    <div className="relative isolate overflow-hidden bg-black flex flex-col items-center justify-center min-h-screen">
      {items?.map((item, i) => {
        const isActive = i === index
        const isLoaded = loadedImages[i]

        return (
          <div
            key={item.id || i}
            className={`absolute inset-0 -z-10 transition-opacity duration-1000 ease-in-out ${
              isActive && isLoaded ? "opacity-75" : "opacity-0"
            }`}
            style={{ backgroundColor: "black" }}
          >
            <Image
              src={item.image.url || "/placeholder.svg"}
              alt={item.text}
              className="h-full w-full object-cover blur-sm scale-105"
              width={1920}
              height={1080}
              draggable={false}
              priority
              onContextMenu={(e) => e.preventDefault()}
              onMouseDown={(e) => e.preventDefault()}
              onDragStart={(e) => e.preventDefault()}
              onLoad={() => setLoadedImages((prev) => ({ ...prev, [i]: true }))}
            />
          </div>
        )
      })}

      <div className="relative z-10">
        <div className="flex flex-col items-center justify-center space-y-4">
          <Image
            src="/cw_logo.svg"
            alt="Combat Warriors Logo"
            width={350}
            height={350}
            className={`transition duration-500 ease-in-out cursor-default mt-12 ${
              items && items[index]?.hideLogo ? "opacity-0" : "opacity-100"
            }`}
            priority
            draggable={false}
            onContextMenu={(e) => e.preventDefault()}
            onMouseDown={(e) => e.preventDefault()}
            onDragStart={(e) => e.preventDefault()}
          />
        </div>
      </div>

      <div className="absolute inset-0 bg-gradient-to-t from-black/75 to-transparent" />

      <div className="absolute bottom-0 w-full xl:w-1/3 lg:w-1/2 p-4 md:p-8 z-10">
        {items?.length > 0 && (
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between space-x-4">
              {items.length > 1 && (
                <button
                  onClick={handlePrev}
                  className="p-2 rounded-full hover:bg-white/10 transition-colors flex-shrink-0"
                >
                  <ArrowLeft className="h-6 w-6 text-white" />
                </button>
              )}
              <div className="flex-1 m-auto text-center">
                <h1 className="text-xl md:text-2xl font-bold text-white text-center truncate">
                  {items[index]?.text}
                </h1>
                {items[index]?.subText && (
                  <p className="text-sm text-gray-300 text-center">{items[index].subText}</p>
                )}
                {items[index]?.buttonText && (
                  <button
                    className="mt-4 px-6 py-2 bg-white text-black rounded-xl hover:bg-gray-200 transition-colors"
                    onClick={() => {
                      if (items[index]?.buttonLink) {
                        window.open(items[index].buttonLink, "_blank")
                      } else {
                        toast.error("Button link not provided. Please notify a staff member.")
                      }
                    }}
                  >
                    {items[index]?.buttonText || "Learn More"}
                  </button>
                )}
              </div>
              {items.length > 1 && (
                <button
                  onClick={handleNext}
                  className="p-2 rounded-full hover:bg-white/10 transition-colors flex-shrink-0"
                >
                  <ArrowRight className="h-6 w-6 text-white" />
                </button>
              )}
            </div>
            {items.length > 1 && (
              <div className="mt-2 w-full bg-white/20 rounded-full h-1 overflow-hidden">
                <motion.div
                  key={index}
                  className="bg-white h-1 rounded-full"
                  initial={{ width: "0%" }}
                  animate={{ width: "100%" }}
                  transition={{
                    duration: slideDuration / 1000,
                    ease: "linear",
                  }}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
